<!-- eslint-disable vue/no-mutating-props -->
<script setup lang="ts">
import type { SiteBlock, SitePricingPlan } from '../../../types/site'

const props = defineProps<{ block: SiteBlock }>()

const t = useSiteEditorT()

const addPlan = () => {
  props.block.data.plans = [...(props.block.data.plans ?? []), { name: '', price: '', features: [] }]
}
const removePlan = (i: number) => {
  props.block.data.plans?.splice(i, 1)
}

const addFeature = (plan: SitePricingPlan) => {
  plan.features = [...(plan.features ?? []), '']
}
const removeFeature = (plan: SitePricingPlan, fi: number) => {
  plan.features?.splice(fi, 1)
}
</script>

<template>
  <!-- eslint-disable vue/no-mutating-props -->
  <UFormField :label="t('site.f.common.heading_optional', 'Heading (optional)')">
    <UInput
      v-model="block.data.headline"
      class="w-full"
    />
  </UFormField>
  <UFormField :label="t('site.f.common.intro_optional', 'Intro (optional)')">
    <UTextarea
      v-model="block.data.subtext"
      :rows="2"
      class="w-full"
    />
  </UFormField>

  <SiteEditorArrayItemCard
    v-for="(plan, i) in block.data.plans ?? []"
    :key="i"
    :label="`${t('site.f.pricing.item_label', 'Plan')} ${i + 1}`"
    @remove="removePlan(i)"
  >
    <UInput
      v-model="plan.name"
      :placeholder="t('site.f.pricing.plan_name', 'Plan name')"
      class="w-full"
    />
    <div class="grid grid-cols-2 gap-2">
      <UInput
        :model-value="plan.price ?? ''"
        :placeholder="t('site.f.pricing.99_000', '₮99,000')"
        class="w-full"
        @update:model-value="plan.price = String($event)"
      />
      <UInput
        :model-value="plan.period ?? ''"
        :placeholder="t('site.f.pricing.month', '/month')"
        class="w-full"
        @update:model-value="plan.period = String($event)"
      />
    </div>
    <UTextarea
      :model-value="plan.description ?? ''"
      :rows="2"
      :placeholder="t('site.f.pricing.short_description', 'Short description')"
      class="w-full"
      @update:model-value="plan.description = String($event)"
    />
    <UInput
      :model-value="plan.badge ?? ''"
      :placeholder="t('site.f.pricing.badge_e_g_most_popular', 'Badge, e.g. Most popular')"
      class="w-full"
      @update:model-value="plan.badge = String($event)"
    />

    <div class="flex items-center justify-between gap-2 py-1">
      <span class="text-xs text-muted">{{ t('site.f.pricing.highlight_plan', 'Highlight this plan') }}</span>
      <USwitch v-model="plan.highlighted" />
    </div>

    <div class="space-y-1.5">
      <p class="text-xs font-medium text-muted">
        {{ t('site.f.pricing.features_label', 'Features') }}
      </p>
      <div
        v-for="(feature, fi) in plan.features ?? []"
        :key="fi"
        class="flex items-center gap-1.5"
      >
        <UInput
          :model-value="feature"
          :placeholder="t('site.f.pricing.what_s_included', 'What\'s included')"
          class="w-full"
          @update:model-value="plan.features![fi] = String($event)"
        />
        <UButton
          size="xs"
          variant="ghost"
          color="error"
          icon="i-lucide-x"
          @click="removeFeature(plan, fi)"
        />
      </div>
      <UButton
        size="xs"
        variant="outline"
        color="neutral"
        icon="i-lucide-plus"
        @click="addFeature(plan)"
      >
        {{ t('site.f.pricing.add_feature', 'Add feature') }}
      </UButton>
    </div>

    <div class="grid grid-cols-2 gap-2">
      <UInput
        :model-value="plan.button_text ?? ''"
        :placeholder="t('site.f.common.button_text', 'Button text')"
        class="w-full"
        @update:model-value="plan.button_text = String($event)"
      />
      <UInput
        :model-value="plan.button_url ?? ''"
        :placeholder="t('site.f.common.url_placeholder', 'https://...')"
        class="w-full"
        @update:model-value="plan.button_url = String($event)"
      />
    </div>
  </SiteEditorArrayItemCard>

  <UButton
    size="sm"
    variant="outline"
    color="neutral"
    icon="i-lucide-plus"
    @click="addPlan"
  >
    {{ t('site.f.pricing.add_item', 'Add plan') }}
  </UButton>
</template>
